import type { ApiTokenIssued } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatStamp } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/** The Claude connector (owner): make a token for a user, see it once, revoke it later. */
export function ConnectorPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const tokens = useQuery({ queryKey: qk.apiTokens, queryFn: ({ signal }) => api.apiTokens(signal) });
  const users = useQuery({ queryKey: qk.users, queryFn: ({ signal }) => api.users(signal) });
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<ApiTokenIssued | null>(null);
  const [copied, setCopied] = useState(false);
  const make = useMutation({
    mutationFn: () => api.createApiToken({ userId, name: name.trim() }),
    onSuccess: (tok) => {
      setIssued(tok);
      setName('');
      setCopied(false);
      void qc.invalidateQueries({ queryKey: qk.apiTokens });
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.revokeApiToken(id), onSuccess: () => void qc.invalidateQueries({ queryKey: qk.apiTokens }) });

  return (
    <div className="stack">
      <PageHead title={t('connectorTitle')} back="/" />
      <p className="muted">{t('connectorHint')}</p>
      {issued ? (
        <section className="card stack-sm">
          <h2>{t('tokenOnce')}</h2>
          <p className="muted">{t('tokenOnceHint')}</p>
          <code className="pre token" data-testid="issued-token">
            {issued.token}
          </code>
          <button
            type="button"
            className="btn btn-primary btn-lg btn-block"
            onClick={() => {
              void navigator.clipboard?.writeText(issued.token).then(() => setCopied(true));
            }}
          >
            {copied ? `✓ ${t('copied')}` : t('copyToken')}
          </button>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setIssued(null)}>
            {t('done')}
          </button>
        </section>
      ) : null}
      <section className="card stack-sm">
        <h2>{t('makeToken')}</h2>
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (userId && name.trim()) make.mutate();
          }}
        >
          <label className="field">
            <span>{t('tokenFor')}</span>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label={t('tokenFor')}>
              <option value="">—</option>
              {(users.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.role}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('tokenName')}</span>
            <input type="text" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} placeholder="Claude on JL's laptop" />
          </label>
          {make.error ? <ErrorBox error={make.error} /> : null}
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={make.isPending || !userId || !name.trim()}>
            {make.isPending ? t('sending') : t('makeToken')}
          </button>
        </form>
      </section>
      <section className="card stack-sm">
        <h2>{t('tokens')}</h2>
        <QueryView query={tokens}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {rows.map((tok) => (
                  <li key={tok.id} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {tok.name} <span className="label">{tok.prefix}…</span>
                        {tok.kind === 'oauth' ? <span className="badge badge-ok">{t('connectedApp')}</span> : null}
                      </span>
                      <span className="row-sub">
                        {tok.userName} · {tok.role} · {formatStamp(tok.createdAt, intlLocale, 'date')}
                        {tok.lastUsedAt ? ` · ${t('lastUsed')} ${formatStamp(tok.lastUsedAt, intlLocale, 'date')}` : ''}
                        {tok.expiresAt && !tok.revokedAt ? ` · ${t('expires')} ${formatStamp(tok.expiresAt, intlLocale, 'date')}` : ''}
                        {tok.revokedAt ? <span className="badge badge-danger">{t('revoked')}</span> : null}
                      </span>
                    </span>
                    {tok.revokedAt ? null : (
                      <button type="button" className="btn btn-ghost" onClick={() => { if (window.confirm(`${t('revoke')}: ${t('confirmSure')}`)) revoke.mutate(tok.id); }} disabled={revoke.isPending}>
                        {t('revoke')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )
          }
        </QueryView>
      </section>
      <section className="card stack-sm">
        <h2>{t('howToConnect')}</h2>
        <p className="muted">{t('howToConnectHint')}</p>
        <code className="pre">{`claude mcp add --transport http morales ${window.location.origin}/api/mcp --header "Authorization: Bearer <token>"`}</code>
      </section>
    </div>
  );
}
